import { PureQueryOptions, useMutation } from '@apollo/client';
import { FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import invariant from 'tiny-invariant';
import { z } from 'zod';
import { graphql } from '~/apollo/generated/v4';
import { Button } from '~/components/ui/button';
import { HookFormErrors } from '~/components/ui/forms/HookFormErrors';
import { useZodForm } from '~/utils/forms';
import type {
  TourSlide,
} from '~/apollo/generated/v4/graphql';
import { VftSlideFormFields } from './vft-slide-form-fields';
import { useVftPageContext, VftSlideFormValues, vftSlideSchema } from '~/utils/modules/vft';
import { getCameraCartographic } from '~/components/cesium/cesiumUtils';
import type { Viewer } from 'cesium';
import { createRefetchQueries } from '~/utils/graphql';
import { VFT_PAGE_QUERY } from '~/routes/upload/vft-manager/$vftId';
import React, { ReactNode } from 'react';
import { useModalState } from '~/hooks/modal';
import { Modal } from '~/components/common/Modal';

const CREATE_VFT_SLIDE = graphql(`
  mutation CreateVftSlide($input: TourSlideCreateInput!) {
    tourSlideCreate(input: $input) {
      result {
        id
        lifecycleStatus
        position
        text
        title
      }
    }
  }
`);

const SAVE_SLIDE_CAMERA = graphql(`
  mutation SaveSlideCamera($id: ID!, $input: TourSlideSaveDefaultCameraInput!) {
    tourSlideSaveDefaultCamera(id: $id, input: $input) {
      result {
        id
        lifecycleStatus
        position
        text
        title
      }
    }
  }
`);

function initialVftSlide(vftSlide?: TourSlide): VftSlideFormValues {
  return {
    title: vftSlide?.title ?? '',
    text: vftSlide?.text ?? '',
  };
}

export function CreateVftSlideModal({
  vftId,
  viewer,
  successCallback,
  children
}: {
  vftId: number;
  viewer: Viewer | null;
  successCallback?: (id: string) => void;
  children: (showModal: () => void) => ReactNode;
}) {
    const { show, showModal, hideModal } = useModalState();
  const [createVftSlide, { loading, error }] = useMutation(CREATE_VFT_SLIDE);
  const [
    saveSlideCamera,
    { loading: saveCameraLoading },
  ] = useMutation(SAVE_SLIDE_CAMERA);
    const refetchQueries = createRefetchQueries([
      {
        query: VFT_PAGE_QUERY,
        variables: { id: vftId },
      },
    ]);

  const form = useZodForm({
    schema: vftSlideSchema,
    values: initialVftSlide(),
  });

  const handleSubmit = form.handleSubmit(
    async (values: z.infer<typeof vftSlideSchema>) => {
      const { ...input } = values;
      const camera = getCameraCartographic(viewer);
      try {
        const res = await createVftSlide({
          variables: {
            input: {
              ...input,
              virtualFieldTripId: vftId,
            },
          },
        });
        const id = res.data?.tourSlideCreate?.result?.id;
        if (!id) {
          toast.error('Saving new slide failed');
          return;
        }
        if (!camera) {
          toast.error('Saving slide camera failed');
          return;
        }
        await saveSlideCamera({
          variables: {
            id,
            input: {
              location: camera,
            },
          },
          refetchQueries: refetchQueries
        });
        toast.success('New Slide Saved');
        form.reset();
        hideModal();
        if (successCallback) {
          successCallback(id);
        }
      } catch (err) {
        console.log('Error saving slide', err);
        toast.error('There was a problem saving the slide.');
      }
    },
  );

  return (
    <>
    {children(showModal)}
    <Modal open={show} onHide={hideModal}>
    <FormProvider {...form}>
      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <VftSlideFormFields create={true}/>
        <HookFormErrors graphQLError={error} />

        <div className="text-center m-2 space-x-2">
          <Button type="submit" color="primary" disabled={loading || saveCameraLoading}>
            Save
          </Button>
          <Button type="button" color="neutral" disabled={loading || saveCameraLoading} onClick={hideModal}>
            Cancel
          </Button>
        </div>
      </form>
    </FormProvider>
    </Modal>
  </>
  );
}
